/**
 * Resolves where each site should be drawn on a map.
 *
 * A site whose street address could not be geocoded still usually names a
 * city, state or postal code, and the server resolves that area's centre
 * into `approxLatitude`/`approxLongitude`. Using it as a display fallback
 * keeps the site visible instead of silently dropping it off the map.
 *
 * Shared by the Sites tab and the standalone evaluation map so the same
 * evaluation never shows a different number of pins on the two screens.
 *
 * Pure — no React, no store — so the placement rules can be tested directly.
 */

export interface SiteWithPosition {
	_id?: string
	latitude?: number | null
	longitude?: number | null
	approxLatitude?: number | null
	approxLongitude?: number | null
	approxLabel?: string | null
	[key: string]: any
}

/** A site with a definite position, plus how trustworthy that position is. */
export interface MappableSite extends SiteWithPosition {
	latitude: number
	longitude: number
	/** True when the pin is an area centre, not the site itself. */
	isApproximate: boolean
	/** The area the pin is the centre of, e.g. "78701, Austin, Texas". */
	approxLabel?: string | null
}

const isFinitePair = (lat: unknown, lng: unknown): boolean =>
	typeof lat === 'number' &&
	typeof lng === 'number' &&
	Number.isFinite(lat) &&
	Number.isFinite(lng)

/**
 * The position to draw a site at, or null when it cannot be placed at all.
 * Precise coordinates always win; the area centre is only a fallback.
 */
export function resolveSitePosition(
	site: SiteWithPosition | null | undefined,
): MappableSite | null {
	if (!site) return null

	if (isFinitePair(site.latitude, site.longitude)) {
		return {
			...site,
			latitude: site.latitude as number,
			longitude: site.longitude as number,
			isApproximate: false,
		}
	}

	if (isFinitePair(site.approxLatitude, site.approxLongitude)) {
		return {
			...site,
			latitude: site.approxLatitude as number,
			longitude: site.approxLongitude as number,
			isApproximate: true,
		}
	}

	return null
}

/** Every site that can be drawn, in the order given. */
export function toMappableSites(sites: SiteWithPosition[] = []): MappableSite[] {
	return sites
		.map(resolveSitePosition)
		.filter((s): s is MappableSite => s !== null)
}

/**
 * Sites eligible for trade-area rings and overlap zones.
 *
 * Approximate sites are deliberately EXCLUDED. A 3-mile ring drawn around a
 * postal-code centroid looks identical to one drawn around a real address,
 * and the whole point of the comparison surface is deciding between sites on
 * exactly that kind of evidence. Showing the pin is helpful; implying the
 * catchment is measured would not be.
 */
export function toPreciseSites(sites: SiteWithPosition[] = []): MappableSite[] {
	return toMappableSites(sites).filter((s) => !s.isApproximate)
}
